/**
 * @module Components.Routes
 *
 */
import React, { useState, useEffect, useRef } from "react";
import { MessageDescriptor } from "react-intl";
import { Promise, resolve, reject } from "rsvp";
import { Link, Redirect, useLocation, useParams } from "react-router-dom";
import { useUrlSearch, useError, usePending } from "app/utils/hooks";
import { AuthService, PhoneVerificationService } from "app/services";
import {
  UserModel,
  TeamModel,
  InvitationModel,
  VerificationModel,
} from "app/models";
import { fetchPost, fetchGet } from "app/utils/request";
import { routeNative } from "app/utils/router";
import SignUpForm from "./SignUpForm";
import BillingInfo from "./BillingInfo";
import AboutYou from "./AboutYou";
import CoachPlanQuestion from "./_components/CoachPlanQuestion";
import InvitedGate from "./_components/InvitedGate";
import InviteDeadEnd from "./_components/InviteDeadEnd";
import AthleteSidePanel from "./_components/AthleteSidePanel";
import TeamAvatar from "app/components/TeamAvatar";
import Styles from "./styles.module.scss";
import config from "config/environment";
import FormItem from "app/components/inputs/FormItem";
import ResendButton from "app/components/PhoneVerification/ResendButton";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { IUserPhone } from "app/interfaces/UserTypes";
import { Trans, useTranslation } from "react-i18next";
import Wrapper from "app/components/Onboarding/SignUpWrapper";
import { cls } from "app/utils";
import {
  planFromCheckoutUrl,
  SignupMethod,
  trackCoachQuestionViewed,
  trackCoachTypeSelected,
  trackInviteAnswered,
  trackInviteQuestionViewed,
  trackPlanMatched,
  trackRoleQuestionViewed,
  trackRoleSelected,
  trackSignupCompleted,
  trackSignupStarted,
} from "app/utils/analytics-helpers/signupFunnel";

const Auth = AuthService as any;

const userState: Partial<UserModel> = {
  firstName: "",
  lastName: "",
  email: "",
  password: "",
  kind: "",
  kindTitle: "",
  sport: "",
  ageConsent: true,
  tosConsent: false,
  consentToken: undefined,
  avatar: undefined,
};

type State = {
  user: Partial<UserModel>;
  lockEmail: boolean;
  showSendConfirmationCode: boolean;
  showCodeVerification: boolean;
  showSignUp: boolean;
  showBilling: boolean;
  showAbout: boolean;
  showCoachPlan: boolean;
  showInvited: boolean;
  showInviteDeadEnd: boolean;
  showStartTrial: boolean;
  invalidInvite: boolean;
  verification: Partial<VerificationModel> | null;
  phone: string;
  phoneCountry: string;
  phoneNormalized: string;
};

const initState: State = {
  user: userState,
  lockEmail: false,
  showSendConfirmationCode: false,
  showCodeVerification: false,
  showSignUp: false,
  showBilling: false,
  showAbout: false,
  showCoachPlan: false,
  showInvited: false,
  showInviteDeadEnd: false,
  showStartTrial: false,
  invalidInvite: false,
  verification: null,
  phone: null,
  phoneCountry: null,
  phoneNormalized: null,
};

type OptionalState = {
  showSendConfirmationCode?: boolean;
  showCodeVerification?: boolean;
  showSignUp?: boolean;
  showBilling?: boolean;
  showAbout?: boolean;
  showCoachPlan?: boolean;
  showInvited?: boolean;
  showInviteDeadEnd?: boolean;
  showStartTrial?: boolean;
};

const initTeam: TeamModel | null = null;
const initInvit: InvitationModel | null = null;

export type RouteParamsDef = {
  invitation_token?: string;
  team_token?: string;
  team_role?: string;
  user_role?: string;
  plan?: string;
  sport?: string;
};

type ErrorState = {
  loginError?: MessageDescriptor | string | null;
};

const initError: ErrorState = {
  loginError: null,
};

const WELCOME_COACH_URL = "/getting-started";

const COACH_PAYWALL_URL = "/purchase/pro/golf";

const COACH_PRO_TRIAL = "/trial/pro";

const COACH_ACADEMY_TRIAL = "/trial/academy";

const ATHLETE_ANALYZE_TRIAL = "/trial/analyze";

// disables /trial/pro feature if COACH_PRO_TRIAL_ON_SIGNUP is set to false as well
const COACH_PRO_TRIAL_ENABLED = true;

const COACH_PRO_TRIAL_ON_SIGNUP = true;

/**
 * @class Signup
 *
 */
export default function SignUp() {
  const { t } = useTranslation();
  const location = useLocation();

  const LOCALES = {
    phone_verification_unprocessable_entity: {
      id: "error.signin-phone-verification.unprocessable_entity",
      defaultMessage: t("Signin failed. Please provide a valid phone number"),
    },
    phone_verification_unknown: {
      id: "error.signin-phone-verification.unknown",
      defaultMessage: t("Signin failed. Error unknown"),
    },
  };

  // When a paywall's Back button returns to signup it appends ?resume=<step>
  // so we re-open the matching plan question instead of the account form (the
  // user is already authenticated by then). Derived here so the first render
  // shows the right step with no flash. Anonymous deep links fall through to
  // the normal signup form — a mid-funnel question makes no sense without an
  // account behind it.
  const resumeStep = Auth.isAuthenticated
    ? new URLSearchParams(location.search).get("resume")
    : null;

  // Query the paywalls should keep across the question round-trip (promo,
  // currency, sport, utm...). Flow-control params stay behind; the `from`
  // marker tells the paywall its Back button has a question to return to.
  const funnelQuery = (marker?: string) => {
    const params = new URLSearchParams(location.search);
    ["resume", "return_url", "returnUrl", "from"].forEach((key) =>
      params.delete(key),
    );
    if (marker) {
      params.set("from", marker);
    }
    const query = params.toString();
    return query ? `?${query}` : "";
  };
  const [state, setState] = useState(() => ({
    ...initState,
    showCoachPlan: resumeStep === "coach-plan",
    showInvited: resumeStep === "invited",
  }));
  const [teamState, setTeam] = useState(initTeam);
  const [invitationState, setInvitation] = useState(initInvit);
  const { invitation_token, team_token, team_role, user_role, sport } =
    useParams<RouteParamsDef>();
  const [searchParams] = useUrlSearch();
  const [pending, startPending, stopPending] = usePending();
  const [errorState, addErrors, clearErrors] = useError(initError);
  const codeRef = useRef<HTMLInputElement>(null);
  const isCoachSignup = user_role && user_role === "coach";
  const phoneRequired = searchParams.phoneRequired;

  // Invite links (team or Space) join without picking a plan, so they stay
  // out of the signup funnel events (CN-2742), all but Signup Completed.
  const inFunnel = !team_token && !invitation_token;

  // Funnel events still on their way to Segment. Every redirect out of the
  // flow waits for them (trackNow gives up after 300 ms), so the full page
  // load doesn't drop the events sent just before it. The wait shows the
  // loading screen, so the step can't be answered twice. An event that fails
  // counts as sent: analytics must never stop the signup.
  const pendingEvents = useRef<PromiseLike<void>[]>([]);
  const [leaving, setLeaving] = useState(false);
  // `outsideFunnel` sends the event for invite-link signups too.
  const track = (
    event: () => PromiseLike<void>,
    { outsideFunnel = false } = {},
  ) => {
    if (!inFunnel && !outsideFunnel) {
      return;
    }
    try {
      pendingEvents.current.push(event().then(undefined, () => undefined));
    } catch {
      // the event is lost; the step goes on
    }
  };
  const routeAfterEvents = (url: string) =>
    Promise.all(pendingEvents.current).then(() =>
      routeNative(url, { reload: true }),
    );
  const leave = (url: string) => {
    setLeaving(true);
    return routeAfterEvents(url);
  };

  const pgaPromoUrl = searchParams?.return_url?.includes(
    "/pga/start-trial?promo=pga6",
  );

  // Both spellings arrive in the wild; the authenticated redirect below
  // already honors returnUrl, so promo detection and routing do too.
  const returnUrl = searchParams?.return_url || searchParams?.returnUrl;
  const coachPaywallUrl = returnUrl?.includes(COACH_PAYWALL_URL);
  const startProTrial =
    !COACH_PRO_TRIAL_ENABLED && returnUrl?.includes(COACH_PRO_TRIAL);

  const decodedReturnUrl = decodeURIComponent(returnUrl || "");
  // Back to the page that sent the visitor to signup. A checkout link counts
  // as the plan match, with the plan it sells.
  const leaveToReturnUrl = () => {
    const plan = planFromCheckoutUrl(decodedReturnUrl);
    if (plan) {
      track(() => trackPlanMatched(plan));
    }
    return leave(returnUrl);
  };
  const [, innerSearch] = decodedReturnUrl.split("?");
  const sportQuery = new URLSearchParams(innerSearch).get("sport");
  // Coach checkout links (/trial/pro, /trial/academy, /purchase/pro,
  // /purchase/academy) preset the coach role and skip the role picker; the
  // analyze pages are athlete-side and keep the full AboutYou step so the
  // user can pick athlete/parent.
  // /purchase is matched by plan (unlike /trial) because the prefix is shared
  // with unrelated routes (/purchase/success, /purchase/session/*).
  const analyzeCheckoutSignup = /\/(trial|purchase)\/analyze/.test(
    decodedReturnUrl,
  );
  const coachTrialSignup =
    (decodedReturnUrl.includes("/trial/") ||
      /\/purchase\/(pro|academy|coachpro)/.test(decodedReturnUrl)) &&
    !analyzeCheckoutSignup;
  // Sport from the checkout link's path segment (/trial/pro/golf,
  // /purchase/academy/baseball). The pages match these paths as prefixes.
  const trialPathSport = decodedReturnUrl.match(
    /\/(?:trial|purchase)\/[^/?]+\/([^/?]+)/,
  )?.[1];
  const trialSport = trialPathSport || sportQuery;
  // A sport named in the link skips the sport question; without one the
  // signup shows the sport-only AboutYou step (coach role either way).
  const trialSportProvided = Boolean(trialSport);
  const linkSport = trialSport
    ? trialSport.charAt(0).toUpperCase() + trialSport.slice(1)
    : undefined;

  useEffect(() => {
    if (phoneRequired) {
      return updateUser({
        sport: "Golf",
        kind: "athlete",
        kindTitle: "Athlete",
      });
    }

    if (team_token != null) {
      loadTeam(team_token)
        .then((model) => {
          updateUser({
            sport: model?.sport,
            kind: "athlete",
            kindTitle: "Athlete",
          });
          joinTeam(model);
        })
        .catch(() => setState({ ...state, invalidInvite: true }));
    } else if (invitation_token != null) {
      loadInvitation(invitation_token)
        .then((model: InvitationModel) => {
          if (model?.role === "coach") {
            updateUser({
              sport: model?.sport,
              space_role: "coach",
              kind: "coach",
              kindTitle: "Coach",
            });
          } else if (model?.role === "athlete") {
            updateUser({
              sport: model?.sport,
              kind: "athlete",
              kindTitle: "athlete",
            });
          } else if (model?.role === "contributor") {
            updateUser({
              sport: model?.sport,
              space_role: "contributor",
              kind: "athlete",
              kindTitle: "athlete",
            });
          }
          acceptInvitation(model);
        })
        .catch(() => setState({ ...state, invalidInvite: true }));
    }

    if (coachPaywallUrl && searchParams.return_url) {
      const formattedSport = sport
        ? sport.charAt(0).toUpperCase() + sport.slice(1)
        : "Golf";

      updateUser({
        ...state.user,
        kind: "coach",
        kindTitle: "Coach",
        sport: formattedSport,
      });
    }

    if (
      (startProTrial && searchParams.return_url) ||
      COACH_PRO_TRIAL_ON_SIGNUP
    ) {
      const formattedSport = sportQuery
        ? sportQuery?.charAt(0)?.toUpperCase() + sportQuery?.slice(1)
        : "Golf";

      if (sportQuery?.toLowerCase() === "golf") {
        updateUser({
          ...state.user,
          kind: "coach",
          kindTitle: "Coach",
          sport: formattedSport,
        });
      }
    }

    // A trial link (/trial/pro, /trial/academy — any currency/promo) is coach-only:
    // pre-set kind=coach & sport (from the link, else Golf) before signup. The
    // sport-only AboutYou step shows only when the link names no sport
    // (see accountCreated).
    if (coachTrialSignup) {
      updateUser({
        ...state.user,
        kind: "coach",
        kindTitle: "Coach",
        sport: linkSport || "Golf",
      });
    } else if (analyzeCheckoutSignup && linkSport) {
      // Athlete-side checkout links (/trial|purchase/analyze/<sport>) keep
      // the full role picker but prefill the sport named in the link.
      updateUser({ ...state.user, sport: linkSport });
    }
  }, [location]);

  useEffect(() => {
    // Signed-in visitors are past the form: a resumed question, or the
    // redirect below.
    if (!Auth.isAuthenticated) {
      track(trackSignupStarted);
    }
  }, []);

  // Same order as the step picked for `showing` below: the account steps
  // (form, phone code, parental consent) win over the funnel questions.
  const onAccountStep =
    state.showSignUp ||
    state.showSendConfirmationCode ||
    state.showCodeVerification ||
    state.showBilling;
  const funnelStep = onAccountStep
    ? null
    : state.showAbout
      ? "about"
      : state.showCoachPlan
        ? "coach-plan"
        : state.showInvited
          ? "invited"
          : null;
  useEffect(() => {
    if (funnelStep === "about") {
      track(trackRoleQuestionViewed);
    } else if (funnelStep === "coach-plan") {
      track(trackCoachQuestionViewed);
    } else if (funnelStep === "invited") {
      track(trackInviteQuestionViewed);
    }
  }, [funnelStep]);

  useEffect(() => {
    if (invitationState != null) {
      const email = invitationState.email;
      const phone = invitationState.phone;
      const phoneCountry = invitationState.phoneCountry;
      const phoneNormalized = invitationState.phoneNormalized;
      const [firstName, lastName] = (invitationState.name || " ").split(" ");

      setState({
        ...state,
        user: { ...state.user, email, firstName, lastName },
        phone,
        phoneCountry,
        phoneNormalized,
        showSendConfirmationCode: phone != null && phoneCountry != null,
        lockEmail: phone === null,
      });
    }
  }, [invitationState]);

  const handlePhoneVerification = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    startPending();

    (PhoneVerificationService as any)
      .verify(state.phone, state.phoneCountry)
      .catch(
        (
          err:
            | "phone_verification_unprocessable_entity"
            | "phone_verification_unknown",
        ) => {
          addErrors({ loginError: LOCALES[err] });
          stopPending();
        },
      )
      .then((verification: VerificationModel) => {
        stopPending();
        setState({
          ...state,
          verification,
          showSendConfirmationCode: false,
          showCodeVerification: true,
        });
      });
  };

  const handleCodeConfirmation = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    clearErrors();

    const codeEl = codeRef.current;
    const verification = state.verification;

    if (codeEl == null || verification == null) {
      addErrors({
        loginError: t("Signin failed. Verification code is invalid."),
      });
      return reject();
    }

    const code = codeEl.value.trim();

    startPending();
    return (PhoneVerificationService as any)
      .confirm(verification.id, code)
      .catch(() => {
        addErrors({
          loginError: t("Signin failed. Verification code is invalid."),
        });
        stopPending();

        return reject();
      })
      .then(() => {
        setState({ ...state, showCodeVerification: false, showSignUp: true });
      });
  };

  const handlePhoneInputChange = (
    value: string,
    data: IUserPhone,
    _event: React.FormEvent<HTMLInputElement>,
    formattedValue: string,
  ) => {
    const phoneNormalized = formattedValue;
    const phone = value.substring(data.dialCode.length);
    const phoneCountry = data.countryCode.toUpperCase();

    setState({ ...state, phone, phoneCountry, phoneNormalized });
  };

  const joinTeam = (team: TeamModel, search?: string): void => {
    search = search || "";
    if (Auth.isAuthenticated) {
      fetchGet(`/teams/${team.id}`)
        .catch(() => resolve())
        .then((data) => {
          if (data == null) {
            const query =
              team_role != null ? { encrypted_role: team_role } : {};
            fetchPost(`/teams/${team_token}/join`, query, { version: 2 })
              .then(() => routeAfterEvents(`/groups/${team.id}${search}`))
              .catch(() => routeAfterEvents(`/groups${search}`));
          } else {
            // Auth.invalidate(true);
            setTeam(team);
          }
        });
    } else {
      setTeam(team);
    }
  };

  const acceptInvitation = (
    invitation: InvitationModel,
    search?: string,
  ): void => {
    search = search || "";
    if (Auth.isAuthenticated) {
      Auth.getAuthUser().then((user: UserModel) => {
        if (
          invitation.email === user.email ||
          (user.phoneVerified && invitation.phone === user.phone)
        ) {
          const afterUrl =
            invitation.object === "team_invitation"
              ? `/groups/${invitation.teamId}`
              : `/spaces/${invitation.spaceId}`;
          const afterErrorUrl =
            invitation.object === "team_invitation" ? `/groups` : `/spaces`;
          const query = { id: invitation.id };
          fetchPost(`/invitations/${invitation.id}/accept`, query, {
            version: 1,
          })
            .then(() => routeAfterEvents(afterUrl + search))
            .catch(() => routeAfterEvents(afterErrorUrl + search));
        } else {
          // Auth.invalidate(true);
          setInvitation(invitation);
        }
      });
    } else {
      setInvitation(invitation);
    }
  };

  const updateUser = (userData: Partial<UserModel>, other?: OptionalState) => {
    const updatedUser = { ...state.user, ...userData };
    setState({ ...state, ...other, user: updatedUser });
  };

  const accountCreated = (data: { user: UserModel }) => {
    // Every account this form creates counts, invite-link signups too. A
    // phone confirmed by SMS on the way here means a phone signup.
    const signedUpUsing: SignupMethod =
      state.verification != null ? "phone" : "email";
    track(() => trackSignupCompleted(signedUpUsing), { outsideFunnel: true });

    // Trial signups keep the coach role but still get the sport question
    // (AboutYou in sport-only mode) before continuing to the trial checkout —
    // unless the link already named the sport, then the step is skipped
    // (the preset coach kind routes straight through below).
    if (coachTrialSignup && !trialSportProvided) {
      return updateUser(data.user, { showSignUp: false, showAbout: true });
    }

    if (
      (state?.user?.kind?.toLowerCase() !== "unknown" &&
        state?.user?.kind?.toLowerCase().length > 0) ||
      team_role
    ) {
      return aboutYouCompleted(state?.user);
    }

    if (coachPaywallUrl) {
      return leaveToReturnUrl();
    }

    return updateUser(data.user, { showSignUp: false, showAbout: true });
  };

  const aboutYouCompleted = (data: UserModel) => {
    const kind = (data.kind || "").toLowerCase();

    if (teamState != null) {
      return joinTeam(teamState, "?firstUse=true");
    }
    if (invitationState != null) {
      return acceptInvitation(invitationState, "?firstUse=true");
    }

    // Dedicated links and invites keep their existing targets and bypass the
    // plan-qualifying questions below.
    if (pgaPromoUrl) {
      return leave("/pga/start-trial?promo=pga6");
    }
    if (coachPaywallUrl || startProTrial || returnUrl) {
      return leaveToReturnUrl();
    }

    // Organic signup: qualify by role and route into the matching in-component
    // question before any paywall. This replaces the old hardcoded /trial/pro
    // tail that bounced athletes/parents off the coach-gated PRO paywall.
    if (kind === "coach") {
      return updateUser(data, { showAbout: false, showCoachPlan: true });
    }
    if (kind === "athlete" || kind === "other") {
      return updateUser(data, { showAbout: false, showInvited: true });
    }

    // Unknown role — drop them into the app rather than a paywall.
    updateUser(data, { showAbout: false });
    leave(WELCOME_COACH_URL);
  };

  if (
    leaving ||
    (!state.invalidInvite &&
      !invitationState &&
      !teamState &&
      (team_token || invitation_token))
  ) {
    return <Wrapper loading={true}></Wrapper>;
  }

  // Join and redirect to groups/spaces if user has already an account
  if (
    (team_token || invitation_token) &&
    Auth.isAuthenticated &&
    !state.showAbout
  ) {
    if (teamState != null) {
      const targetUrl = "/groups/" + teamState.id;
      joinTeam(teamState);
      routeNative(targetUrl, { reload: true });
    } else if (invitationState != null) {
      const targetUrl =
        invitationState.object === "team_invitation"
          ? `/groups/${invitationState.teamId}`
          : `/spaces/${invitationState.spaceId}`;
      acceptInvitation(invitationState);
      routeNative(targetUrl, { reload: true });
    }
  }

  if (
    !team_token &&
    !invitation_token &&
    Auth.isAuthenticated &&
    !state.showAbout &&
    !state.showCoachPlan &&
    !state.showInvited &&
    !state.showInviteDeadEnd
  ) {
    if (searchParams.return_url) {
      return <Redirect to={searchParams.return_url} />;
    } else if (searchParams.returnUrl) {
      return <Redirect to={searchParams.returnUrl} />;
    }
    const defaultReturnUrl = "?firstUse=true&kind=" + state.user.kind;
    return <Redirect to={defaultReturnUrl} />;
  }

  const scComp = (
    <div>
      <div>{t("Please enter your phone number to continue")}</div>

      <form
        noValidate
        onSubmit={(e) => handlePhoneVerification(e)}
        target="_self"
      >
        <FormItem>
          <PhoneInput
            country={
              invitationState && invitationState?.phoneCountry
                ? invitationState?.phoneCountry?.toLowerCase()
                : ""
            }
            value={invitationState ? invitationState.phoneNormalized : ""}
            countryCodeEditable={false}
            onChange={handlePhoneInputChange}
            inputStyle={{ width: "100%", height: "42px" }}
            inputProps={{ autoFocus: true }}
          />
        </FormItem>

        <FormItem error={errorState.loginError}>
          <button
            className="btn btn-primary full signin mt-0"
            disabled={pending.status}
          >
            {t("Continue")}
          </button>
        </FormItem>
      </form>
    </div>
  );

  const cvComp = (
    <div>
      <div className={Styles.confirmationSentText}>
        (
        <Trans
          i18nKey="confirmation_code_sent"
          components={{ strong: <strong />, br: <br /> }}
          values={{ phoneNumber: state.phoneNormalized }}
        />
        )
      </div>

      <form
        noValidate
        onSubmit={(e) => handleCodeConfirmation(e)}
        target="_self"
      >
        <FormItem>
          <input
            className="code"
            name="code"
            ref={codeRef}
            placeholder={t("Verification Code")}
            type="text"
          />
        </FormItem>

        <ResendButton phone={state.phone} phoneCountry={state.phoneCountry} />

        <FormItem error={errorState.loginError}>
          <button
            className="btn btn-primary full signin mt-0"
            disabled={pending.status || !state.verification}
          >
            {t("Next")}
          </button>
        </FormItem>
      </form>
    </div>
  );

  const sfComp = (
    <SignUpForm
      user={state.user}
      verificationToken={state.verification ? state.verification.token : null}
      phoneCountry={state.phoneCountry}
      phoneNormalized={state.phoneNormalized}
      phoneRequired={phoneRequired}
      lockEmail={state.lockEmail}
      onUserChange={updateUser}
      onDone={accountCreated}
      inFunnel={inFunnel}
      onSignin={(user: UserModel) =>
        updateUser(user, { showBilling: false, showAbout: false })
      }
      onConsent={(user: UserModel) => updateUser(user, { showBilling: true })}
      hideOutsideSources={teamState != null || invitationState != null}
      coachSignup={isCoachSignup}
    />
  );

  const biComp = (
    <BillingInfo
      onDone={(token: string) =>
        updateUser({ consentToken: token }, { showBilling: false })
      }
      onCancel={() => setState({ ...state, showBilling: false })}
    />
  );

  const aboutComp = (
    <AboutYou
      user={state.user}
      coachSignup={isCoachSignup}
      onComplete={(data: Partial<UserModel> & { title?: string | null }) => {
        track(() => trackRoleSelected(data.kind, data.sport, data.title));
        aboutYouCompleted(data as UserModel);
      }}
      sportOnly={coachTrialSignup}
    />
  );

  const coachPlanComp = (
    <CoachPlanQuestion
      onSelectPlan={(target) => {
        track(() => trackCoachTypeSelected(target));
        track(() => trackPlanMatched(target));
        leave(
          (target === "academy" ? COACH_ACADEMY_TRIAL : COACH_PRO_TRIAL) +
            funnelQuery("coach-plan"),
        );
      }}
      onBack={
        // Resumed from a paywall: the account/about steps are already done,
        // so there is nothing to go back to.
        resumeStep === "coach-plan"
          ? undefined
          : () => setState({ ...state, showCoachPlan: false, showAbout: true })
      }
    />
  );

  const invitedComp = (
    <InvitedGate
      onAnswer={(answer) => {
        track(() => trackInviteAnswered(answer));
        if (answer === "no") {
          track(() => trackPlanMatched("analyze"));
          // No marker: the analyze paywall intentionally has no Back (spec).
          leave(ATHLETE_ANALYZE_TRIAL + funnelQuery());
          return;
        }
        // The "check with your coach" screen is where an invited athlete's
        // signup ends.
        track(() => trackPlanMatched("invited_join"));
        setState({ ...state, showInvited: false, showInviteDeadEnd: true });
      }}
      onBack={
        // Resumed from a paywall: the account/about steps are already done,
        // so there is nothing to go back to.
        resumeStep === "invited"
          ? undefined
          : () => setState({ ...state, showInvited: false, showAbout: true })
      }
    />
  );

  const deadEndComp = (
    <InviteDeadEnd
      onBack={() =>
        setState({ ...state, showInviteDeadEnd: false, showInvited: true })
      }
    />
  );

  let showing = state.showSendConfirmationCode ? cvComp : sfComp;

  if (state.showSignUp) {
    showing = sfComp;
  } else if (state.showSendConfirmationCode) {
    showing = scComp;
  } else if (state.showCodeVerification) {
    showing = cvComp;
  } else if (state.showBilling) {
    showing = biComp;
  } else if (state.showAbout) {
    showing = aboutComp;
  } else if (state.showCoachPlan) {
    showing = coachPlanComp;
  } else if (state.showInvited) {
    showing = invitedComp;
  } else if (state.showInviteDeadEnd) {
    showing = deadEndComp;
  } else if (state.invalidInvite) {
    showing = <InvalidInvite />;
  }

  let header = <div className={Styles.teamHeader}></div>;
  if (teamState != null) {
    header = (
      <InviteHeader
        type="team"
        name={teamState.name || ""}
        logo={teamState.logo}
        sport={teamState.sport}
      />
    );
  } else if (invitationState != null) {
    if (invitationState.object === "team_invitation") {
      header = (
        <InviteHeader
          type="invitation_team"
          name={invitationState.teamName}
          sport={invitationState.sport}
          user={invitationState.name}
          createdBy={invitationState.inviterName}
        />
      );
    } else {
      header = (
        <InviteHeader
          type="invitation_space"
          name={invitationState.spaceName || ""}
          user={invitationState.name}
          createdBy={invitationState.inviterName}
        />
      );
    }
  }

  const wrapperHeader = (
    <div className={Styles.wrapper_header_container}>
      <Trans
        i18nKey="sign_up_header"
        components={{ strong: <strong /> }}
        values={{ coachName: searchParams.coach_name }}
      />
    </div>
  );

  // The plan questions keep the default "Welcome to CoachNow" panel like the
  // rest of the signup; only the invite dead end explains itself in the panel.
  let sideContent: JSX.Element | undefined;
  if (state.showInviteDeadEnd) {
    sideContent = <AthleteSidePanel />;
  }

  return (
    <Wrapper
      header={searchParams.coach_name ? wrapperHeader : null}
      sideContent={sideContent}
      wide={state.showCoachPlan || state.showInvited || state.showInviteDeadEnd}
    >
      {header}
      {showing}
    </Wrapper>
  );
}

type InviteProps = {
  type: string;
  name: string;
  user?: string;
  logo?: string;
  sport?: string;
  createdBy?: string;
};

const InviteHeader = (props: InviteProps) => {
  const { t } = useTranslation();

  let body = <div />;
  if (props.type === "team") {
    body = (
      <div>
        <Trans
          i18nKey="signup_join_group"
          components={{ strong: <strong /> }}
          values={{ name: `${props.name} / ${props.sport}` }}
        />
      </div>
    );
  } else if (props.type === "invitation_team") {
    const name =
      props.sport != null ? `${props.name} / ${props.sport}` : props.name;
    body = (
      <div>
        <p>
          <Trans
            i18nKey="signup_join_group"
            components={{ strong: <strong /> }}
            values={{ name }}
          />
        </p>
        <p>{t("created by {{creator}}", { creator: props.createdBy })}</p>
      </div>
    );
  } else if (props.type === "invitation_space") {
    body = (
      <div>
        <p>
          <Trans
            i18nKey="signup_join_trainingspace"
            components={{ strong: <strong /> }}
            values={{ name: props.name }}
          />
        </p>
        <p>{t("created by {{creator}}", { creator: props.createdBy })}</p>
      </div>
    );
  }

  let header = <div />;
  if (props.logo) {
    header = (
      <TeamAvatar
        className="avatar"
        srcName={props.logo}
        altName={props.name}
      />
    );
  } else if (props.user) {
    header = (
      <h4>
        {t("Welcome")}
        &nbsp;{props.user}
      </h4>
    );
  }

  return (
    <div className={Styles.teamHeader}>
      <div className={cls(Styles.avatar, Styles.form_header_avatar)}>
        {header}
      </div>
      <div className={Styles.message}>{body}</div>
    </div>
  );
};

const InvalidInvite = () => {
  const { t } = useTranslation();

  return (
    <div className={Styles.invalidInvite}>
      <h1 className="text-danger">{t("Invitation is Invalid")}</h1>

      <div className={Styles.message}>
        {t("This invitation has expired or it has already been accepted.")}
        &nbsp;
        <Trans
          i18nKey="help_email"
          components={{ a: <a href={`mailto:${config.SUPPORT_EMAIL}`} /> }}
          values={{ email: config.SUPPORT_EMAIL, text: config.SUPPORT_EMAIL }}
        />
      </div>

      <div className={Styles.signin}>
        <Link className="btn btn-link full" to="/signin">
          {t("Sign In")}
        </Link>
      </div>
    </div>
  );
};

const loadTeam = (team_token: string) => {
  return fetchGet<TeamModel>(
    `/teams/by_token/${team_token}`,
    {},
    { version: 1 },
  ).then((model: TeamModel | TeamModel[]) => model as TeamModel);
};

export const loadInvitation = (
  invitation_token: string,
): Promise<InvitationModel> => {
  return fetchGet(
    `/invitations/by_token/${invitation_token}`,
    {},
    { version: 1 },
  ).then((model: InvitationModel): InvitationModel => {
    if (model != null && model.id != null) {
      return model;
    } else {
      return reject(null);
    }
  });
};
